import { SDK } from '@bloque/sdk';
import { IS_DEV_DEPLOY, IS_LOCAL } from '~/config/environment';
import { getLocalStorageItem } from '~/utils/local-storage';

export type AuthenticatedBloque = Awaited<ReturnType<SDK['authenticate']>>;

const LOCAL_BASE_URL = import.meta.env.PUBLIC_API_URL || '';

const createBloque = async (): Promise<AuthenticatedBloque> => {
  const token = getLocalStorageItem('access_token') as string;

  const sdk = new SDK({
    auth: {
      type: 'jwt',
    },
    mode: IS_DEV_DEPLOY ? 'sandbox' : 'production',
    tokenStorage: {
      get() {
        return token;
      },
      set() {},
      clear() {},
    },
    ...(IS_LOCAL && LOCAL_BASE_URL && ({ baseUrl: LOCAL_BASE_URL } as any)),
  });

  return await sdk.authenticate();
};

const globalForBloque = globalThis as typeof globalThis & {
  bloqueSdk?: AuthenticatedBloque;
  bloqueSdkPromise?: Promise<AuthenticatedBloque>;
};

export const initBloque = async (): Promise<AuthenticatedBloque> => {
  if (globalForBloque.bloqueSdk) {
    return globalForBloque.bloqueSdk;
  }

  if (!globalForBloque.bloqueSdkPromise) {
    globalForBloque.bloqueSdkPromise = createBloque()
      .then((client) => {
        globalForBloque.bloqueSdk = client;
        return client;
      })
      .catch((error) => {
        globalForBloque.bloqueSdkPromise = undefined;
        throw error;
      });
  }

  return await globalForBloque.bloqueSdkPromise;
};

export const getBloque = (): AuthenticatedBloque => {
  if (!globalForBloque.bloqueSdk) {
    throw new Error(
      'Bloque SDK is not initialized. Call `await initBloque()` first.',
    );
  }

  return globalForBloque.bloqueSdk;
};

export const bloque = new Proxy({} as AuthenticatedBloque, {
  get(_target, prop, receiver) {
    const instance = getBloque();
    const value = Reflect.get(instance as object, prop, receiver);
    return typeof value === 'function' ? value.bind(instance) : value;
  },
  set(_target, prop, value, receiver) {
    return Reflect.set(getBloque() as object, prop, value, receiver);
  },
}) as AuthenticatedBloque;
